import React, { useState } from "react";
import axios from "axios";
import PopModal from "../../../Constant/Custom/Models/PopModal";
import { serverUrl } from "../../../Constant/Static/config";
import Loader from "../../../Constant/Custom/Loader";
import AdminCertUpload from "./AdminCertUpload";

function ImagesUpload({ handlePrevious, handleNext }) {
  const [loading, setLoading] = useState(false);
  const [appno, setAppno] = useState("");
  const [mobile, setMobile] = useState("");
  const [photo, setPhoto] = useState(null);
  const [signature, setSignature] = useState(null);
  const [fileupload, setFileupload] = useState(false);
  const [photoPreview, setPhotoPreview] = useState("");
  const [signaturePreview, setSignaturePreview] = useState("");
  const [onCancel, setOnCancel] = useState(() => () => { });
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState({ title: "", text: "", icon: "" });
  const [onConfirm, setOnConfirm] = useState(() => () => { });

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const handleFileChange = async (e) => {
    setShowCancelModal(false);
    const { name, files } = e.target;
    const file = files[0];
    // console.log(`Uploading: ${name} | File:`, file);

    const validTypes = ["image/jpeg", "image/jpg"];
    const maxPhotoSize = 50 * 1024;
    const maxSignatureSize = 30 * 1024;

    if (!file || !validTypes.includes(file.type)) {
      setModalType({
        title: "Invalid Image Format",
        text: "Please upload a valid image (JPG/PNG).",
        icon: "warning",
      });
      openModal();
      setOnConfirm(() => closeModal);
      return;
    }

    if (name === "photo" && file.size > maxPhotoSize) {
      setModalType({
        title: "Invalid Photo Size",
        text: "Please upload a photo (JPG/JPEG) under 50KB.",
        icon: "warning",
      });
      openModal();
      setOnConfirm(() => closeModal);
      return;
    } else if (name === "signature" && file.size > maxSignatureSize) {
      setModalType({
        title: "Invalid Signature Size",
        text: "Please upload a signature (JPG/JPEG) under 30KB.",
        icon: "warning",
      });
      openModal();
      setOnConfirm(() => closeModal);
      return;
    }

    if (!appno || !mobile) {
      setModalType({
        title: "Missing Information",
        text: "Please enter both Application Number and Mobile Number.",
        icon: "warning",
      });
      openModal();
      setOnConfirm(() => closeModal);
      return;
    }


    if (name === "photo") {
      setPhoto(file);
    } else if (name === "signature") {
      setSignature(file);
    }

    const formData = new FormData();
    formData.append("appno", appno);
    formData.append("mobile", mobile);
    formData.append(name, file);

    try {
      setFileupload(true);
      setLoading(true);
      const response = await axios.post(`${serverUrl}/api/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // console.log("Upload response:", response.data);

      if (response.status === 200) {
        const result = response.data;
        if (result.photoUrl && name === "photo") {
          setPhotoPreview(result.photoUrl);
        } else if (result.signatureUrl && name === "signature") {
          setSignaturePreview(result.signatureUrl);
        } else {
          console.warn(`No URL returned for ${name}`);
        }

        setModalType({
          icon: "success",
          title: "Success",
          text: `${name === "photo" ? "Photo" : "Signature"} Uploaded Successfully`,
        });
        openModal();
        setOnConfirm(() => closeModal);
      } else {
        setModalType({
          icon: "info",
          title: "Upload Failed",
          text: "Failed to upload file. Please refresh and try again.",
        });
        openModal();
        setOnConfirm(() => closeModal);
      }
    } catch (err) {
      console.error("Error uploading file:", err);
      setModalType({
        icon: "error",
        title: "Upload Error",
        text: "Error uploading file. Please try again later.",
      });
      openModal();
      setOnConfirm(() => closeModal);
    } finally {
      setLoading(false);
      setFileupload(false);
    }
  };

  const handleFetchImages = async () => {
    if (!appno || !mobile) {
      setModalType({
        title: "Missing Information",
        text: "Please enter both Application Number and Mobile Number.",
        icon: "warning",
      });
      openModal();
      setOnConfirm(() => closeModal);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${serverUrl}/api/fetchUploadedImages`, {
        appno: appno,
        mobile: mobile,
      });
      // console.log("Fetch response:", response.data);
      if (response.data.photoUrl) {
        setPhotoPreview(response.data.photoUrl);
      }
      if (response.data.signatureUrl) {
        setSignaturePreview(response.data.signatureUrl);
      }
    } catch (err) {
      console.error("Error fetching uploaded images:", err);
      setModalType({
        icon: "error",
        title: "Invalid Application",
        text: "Please Give Correct Information",
      });
      openModal();
      setOnConfirm(() => closeModal);
    } finally {
      setLoading(false);
    }
  };

  const handleFileDelete = async (fileUrl, deletedField) => {
    // console.log(`Deleting field: ${deletedField}, URL: ${fileUrl}`);
    try {
      if (!deletedField) {
        console.warn("No matching field found for fileUrl.");
        return;
      }

      if (!appno || !mobile) {
        setModalType({
          title: "Missing Information",
          text: "Please enter both Application Number and Mobile Number.",
          icon: "warning",
        });
        openModal();
        setOnConfirm(() => closeModal);
        return;
      }

      const deleteFile = async () => {
        try {
          if (deletedField === "photo") {
            setPhotoPreview("");
            setPhoto(null);
          } else if (deletedField === "sign") {
            setSignaturePreview("");
            setSignature(null);
          }
          setLoading(true);
          const response = await axios.post(`${serverUrl}/api/delete-image`, {
            fileUrl,
            filename: deletedField,
            appno: appno,
            mobile: mobile,
          });

          // console.log("File deleted successfully:", response.data?.message || response.statusText);
        } catch (error) {
          console.error("Error deleting file on server:", error);
          setModalType({
            icon: "error",
            title: "Deletion Error",
            text: "Error deleting file. Please try again later.",
          });
          openModal();
          setOnConfirm(() => closeModal);
        } finally {
          setLoading(false);
          closeModal();
        }
      };

      setModalType({
        icon: "warning",
        title: `${deletedField === "photo" ? "Photo" : "Signature"} Deletion`,
        text: `Are you sure you want to delete this ${deletedField === "photo" ? "photo" : "signature"}?`,
      });
      setShowCancelModal(true);
      openModal();
      setOnCancel(() => closeModal);
      setOnConfirm(() => deleteFile);
    } catch (error) {
      console.error("Error preparing file deletion:", error);
    }
  };

  const handleImageUploadSubmit = () => {
    setShowCancelModal(false);
    if (photoPreview && signaturePreview) {
      setModalType({
        icon: "success",
        title: "Success",
        text: "Photo and Signature Uploaded Successfully",
      });
      openModal();
      setOnConfirm(() => {
        closeModal();
        handleNext();
      });
    } else {
      setModalType({
        icon: "warning",
        title: "Incomplete Upload",
        text: "Please upload both Photo and Signature.",
      });
      openModal();
      setOnConfirm(() => closeModal);
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <section>
      <div className="section-head-container">
        <h2 className="section-title">Image and Signature Updation</h2>
      </div>
      <div className="file-upload-container">
        <form>
          <div
            style={{
              marginTop: "40px",
              display: "flex",
              marginBottom: "20px",
            }}
          >
            <div>
              <label htmlFor="appno">
                <span style={{ marginLeft: "20px" }}>Application Number</span>
              </label>
              <input
                type="text"
                id="appno"
                name="appno"
                value={appno}
                style={{
                  marginLeft: "20px",
                  width: "100%",
                  padding: "0.75rem 1rem",
                  border: "1px solid #ccc",
                  borderRadius: "6px",
                  fontSize: "1rem",
                  fontFamily: "inherit",
                  backgroundColor: "#f9f9f9",
                  color: "#333",
                  transition: "border-color 0.3s ease, box-shadow 0.3s ease, background-color 0.3s ease",
                  boxSizing: "border-box",
                }}
                onChange={(e) => setAppno(e.target.value)}
                required
              />
            </div>
            <div style={{
              marginRight: "50PX"
            }}>
              <button
                type="button"
                className="log-btn button-primary"
                onClick={handleFetchImages}
                style={{
                  marginTop: "100px",
                  marginLeft: "100px",
                  padding: "0.7 1.5rem",
                  border: "none",
                  borderRadius: "4px",
                  fontSize: "1rem",
                  fontWeight: 500,
                  cursor: "pointer",
                  transition: "background-color 0.3s ease, transform 0.2s ease",
                  backgroundColor: "#007bff",
                  color: "#fff",
                }}

              >
                Fetch Images
              </button>
            </div>

            <div style={{
              marginRight: "70px"
            }}>
              <label htmlFor="mobile">
                <span>Mobile Number</span>
              </label>
              <input
                type="text"
                id="mobile"
                name="mobile"
                value={mobile}
                style={{
                  marginRight: "130px",
                  width: "100%",
                  padding: "0.75rem 1rem",
                  border: "1px solid #ccc",
                  borderRadius: "6px",
                  fontSize: "1rem",
                  fontFamily: "inherit",
                  backgroundColor: "#f9f9f9",
                  color: "#333",
                  transition: "border-color 0.3s ease, box-shadow 0.3s ease, background-color 0.3s ease",
                  boxSizing: "border-box",
                }}
                onChange={(e) => setMobile(e.target.value)}
                required
              />
            </div>
          </div>
          <div className="file-input">
            <label htmlFor="photo">
              <span>Upload Photo</span>
            </label>
            {!photoPreview ? (
              <input
                type="file"
                id="photo"
                name="photo"
                accept="image/*"
                onChange={handleFileChange}
                required
                disabled={photoPreview}
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  backgroundColor: "#f9f9f9",
                  cursor: "pointer",
                }}
              />
            ) : (
              <>
                <input
                  style={{
                    marginTop: "10px",
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    backgroundColor: "#e0e0e0",
                  }}
                  className="fileUpload-inp"
                  type="text"
                  value="Uploaded"
                  disabled
                />
                <div
                  style={{
                    display: "flex",
                    justifyContent: "right",
                    alignItems: "center",
                    width: "100%",
                    margin: "10px 0",
                  }}
                >
                  <input
                    type="button"
                    className="deleteButton"
                    onClick={() => handleFileDelete(photoPreview, "photo")}
                    value="Delete"
                    style={{
                      padding: "8px 16px",
                      border: "none",
                      borderRadius: "4px",
                      backgroundColor: "#dc3545",
                      color: "#fff",
                      cursor: "pointer",
                    }}
                  />
                </div>
              </>
            )}
          </div>
          <div className="file-input">
            <label htmlFor="signature">
              <span>Upload Signature</span>
            </label>
            {!signaturePreview ? (
              <input
                type="file"
                id="signature"
                name="signature"
                accept="image/*"
                onChange={handleFileChange}
                required
                disabled={signaturePreview}
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  backgroundColor: "#f9f9f9",
                  cursor: "pointer",
                }}
              />
            ) : (
              <>
                <input
                  style={{
                    marginTop: "10px",
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    backgroundColor: "#e0e0e0",
                  }}
                  className="fileUpload-inp"
                  type="text"
                  value="Uploaded"
                  disabled
                />
                <div
                  style={{
                    display: "flex",
                    justifyContent: "right",
                    alignItems: "center",
                    width: "100%",
                    margin: "10px 0",
                  }}
                >
                  <input
                    type="button"
                    className="deleteButton"
                    onClick={() => handleFileDelete(signaturePreview, "sign")}
                    value="Delete"
                    style={{
                      padding: "8px 16px",
                      border: "none",
                      borderRadius: "4px",
                      backgroundColor: "#dc3545",
                      color: "#fff",
                      cursor: "pointer",
                    }}
                  />
                </div>
              </>
            )}
          </div>
        </form>
        <div className="image-preview">
          <div>
            <h3>Photo Preview:</h3>
            {photoPreview ? (
              <img
                src={photoPreview}
                alt="Photo Preview"
                style={{ maxWidth: "200px", maxHeight: "200px" }}
              />
            ) : (
              <p>No photo uploaded</p>
            )}
          </div>
          <div>
            <h3>Signature Preview:</h3>
            {signaturePreview ? (
              <img
                src={signaturePreview}
                alt="Signature Preview"
                style={{ maxWidth: "200px", maxHeight: "200px" }}
              />
            ) : (
              <p>No signature uploaded</p>
            )}
          </div>
        </div>
      </div>
      <br />
      {/* <AdminCertUpload appNo = {appno} mobile={mobile}/> */}
      <div
        style={{
          position: "relative",
          alignSelf: "center",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <PopModal
          isOpen={isModalOpen}
          type={modalType}
          confirmButtonText="OK"
          onConfirm={onConfirm}
          showCancelButton={showCancelModal}
          cancelButtonText="Cancel"
          onCancel={onCancel}
        />
      </div>
    </section>
  );
}

export default ImagesUpload;